import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import { useEnableProjectShare, useProjectShare, useRevokeProjectShare } from '../../hooks/useOps';

function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

/**
 * "Share with client" control in the project workspace header. Creates a
 * read-only progress link (no money, no internal data), copies it, and can
 * revoke it — re-enabling later issues a NEW token, so old links stay dead.
 */
export function ShareLinkButton({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: share } = useProjectShare(open ? projectId : undefined);
  const enable = useEnableProjectShare();
  const revoke = useRevokeProjectShare();

  const active = share && !share.revokedAt ? share : null;

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const copy = async () => {
    if (!active) return;
    await navigator.clipboard.writeText(shareUrl(active.token));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Button variant="outlined" startIcon={<ShareIcon />} onClick={() => setOpen(true)}>
        Share
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Client progress link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Anyone with this link sees a read-only progress dashboard: work streams, milestones,
            daily updates and photos. Money, problems and internal notes are never included.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {active ? (
            <Stack gap={1.5}>
              <TextField
                fullWidth
                size="small"
                value={shareUrl(active.token)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                        <IconButton onClick={copy} edge="end" size="small">
                          {copied ? <CheckIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" gap={1}>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(shareUrl(active.token), '_blank')}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  color="error"
                  disabled={revoke.isPending}
                  onClick={() => run(() => revoke.mutateAsync(projectId))}
                >
                  Revoke link
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="contained"
              disabled={enable.isPending}
              onClick={() => run(() => enable.mutateAsync(projectId))}
            >
              {share?.revokedAt ? 'Create new link' : 'Create share link'}
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
